/**
 * Analytics helpers for practice sessions.
 *
 * The SINGLE source of the "scope" properties that say where a practice came from. Three scopes:
 * the volatile single-KC practice of "Áreas de mejora", and the single-LESSON and single-UNIT
 * practices launched from the subject detail programme. The KC scope has one builder per emission
 * point because what is known changes between launch and a loaded session; the lesson and unit
 * scopes are only reported at launch.
 *
 * `kc_id` is ALWAYS the KC's `referenceId`, never its internal uuid — the value the rest of the
 * frontend reports as `kc_id`. Mixing both spaces would break the per-KC breakdown, so an
 * unresolvable KC is reported by OMITTING the property.
 */

import { TOC_STATUS } from '../types/courseToc.constants';

import type { TocStatus } from '../types/courseToc.constants';
import type { PracticeOriginKc } from '../types/practiceCourses';

/** The subset of a loaded practice session the scope properties can be derived from. */
export type PracticeScopeSessionSource = {
  /**
   * Origin KC as resolved by the read DTO (TANAI-2141) — the single source of the practised KC.
   * It survives an empty `sessionKCs` (early abandon) and a rollback, and it is `null` for every
   * session that is not a KC practice.
   */
  originKc?: PracticeOriginKc | null;
};

type BuildLessonLaunchScopePropsParams = {
  lessonId: string;
  lessonStatus: TocStatus;
};

type BuildUnitLaunchScopePropsParams = {
  unitId: string;
  unitStatus: TocStatus;
};

type BuildLoadedSessionScopePropsParams = {
  /**
   * Whether the loaded session is a volatile KC practice. Decided by the caller because it
   * combines the two signals of the read DTO: the session type and the resolved origin KC.
   */
  isKcPractice: boolean;
  /** Loaded session, the source of the practised KC. */
  sessionData?: PracticeScopeSessionSource | null;
};

/** Lesson practice status as REPORTED: a stable, language-independent enum, never UI copy. */
export type LessonAnalyticsStatus = 'blocked' | 'in_practice' | 'in_review' | 'mastered' | 'not_started';

/**
 * Scope properties of a volatile single-KC practice. `kc_id` is omitted (rather than sent
 * empty) when the practised KC cannot be resolved.
 *
 * Kept a separate shape from `PracticeLessonScopeProps` so that typing each builder with what it
 * actually returns makes `kc_id` alongside `scope: 'lesson'` a compile error.
 */
export type PracticeKcScopeProps = {
  is_volatile: true;
  kc_id?: string;
  kc_status: 'blocked';
  origin: 'mi_progreso_kc';
  scope: 'kc';
};

/** Scope of a SINGLE LESSON practice launched from the subject detail. */
export type PracticeLessonScopeProps = {
  lesson_id: string;
  lesson_status: LessonAnalyticsStatus;
  origin: 'mi_progreso_lesson';
  scope: 'lesson';
};

/**
 * Scope of a SINGLE UNIT practice launched from the subject detail: a modules-only unit, whose
 * content hangs directly off it. A shape of its own for the same reason as the lesson one.
 */
export type PracticeUnitScopeProps = {
  origin: 'mi_progreso_unit';
  scope: 'unit';
  unit_id: string;
  unit_status: LessonAnalyticsStatus;
};

/** No scope at all: any practice that is neither a KC, a single-lesson nor a single-unit practice. */
export type PracticeNoScopeProps = Record<string, never>;

/** The fixed scope of an "Áreas de mejora" practice, plus its KC when one is resolved. */
function buildKcScopeProps(kcReferenceId: string): PracticeKcScopeProps {
  const scopeProps: PracticeKcScopeProps = {
    is_volatile: true,
    kc_status: 'blocked',
    origin: 'mi_progreso_kc',
    scope: 'kc',
  };

  if (!kcReferenceId) return scopeProps;

  return { ...scopeProps, kc_id: kcReferenceId };
}

/**
 * Scope properties of the practice being LAUNCHED (`started`). Only the KC launch flow calls this
 * — it has just created the session — so there is no session type to check.
 *
 * `kc_id` comes from the `referenceId` of the origin KC that the create response of
 * POST /activities/sessions/kc resolves (TANAI-2141) — the same value the read DTO reports for
 * `completed` / `discarded`, so the three events of one practice share one space of values. It is
 * omitted (never sent as the internal uuid, which the create response also echoes flat as
 * `originKcId`) if the reference cannot be resolved.
 */
export function buildKcLaunchScopeProps(kcReferenceId?: string | null): PracticeKcScopeProps {
  return buildKcScopeProps(kcReferenceId ?? '');
}

// Reads from `TocStatus`, the API's own enum — NOT the view's `LessonStatusKind`, which collapses
// IN_REVIEW into "mastered" and would merge two states the backend distinguishes.
const LESSON_ANALYTICS_STATUS: Record<TocStatus, LessonAnalyticsStatus> = {
  [TOC_STATUS.BLOCKED]: 'blocked',
  [TOC_STATUS.IN_PRACTICE]: 'in_practice',
  [TOC_STATUS.IN_REVIEW]: 'in_review',
  [TOC_STATUS.MASTERED]: 'mastered',
  [TOC_STATUS.NOT_STARTED]: 'not_started',
};

/**
 * Maps a lesson's OR a modules-only unit's status: both come from the same `TocStatus` enum and
 * are reported with the same values. `normalizeCourseToc` defaults neither, so either can arrive
 * missing at runtime.
 */
export function toLessonAnalyticsStatus(status?: TocStatus): LessonAnalyticsStatus {
  if (!status) return 'not_started';

  return LESSON_ANALYTICS_STATUS[status] ?? 'not_started';
}

/**
 * Scope of the single-lesson practice being LAUNCHED (`started`). No `is_volatile`: this session
 * persists, and the property is being retired.
 */
export function buildLessonLaunchScopeProps({ lessonId, lessonStatus }: BuildLessonLaunchScopePropsParams): PracticeLessonScopeProps {
  return {
    lesson_id: lessonId,
    lesson_status: toLessonAnalyticsStatus(lessonStatus),
    origin: 'mi_progreso_lesson',
    scope: 'lesson',
  };
}

/** Scope of the single-unit practice being LAUNCHED (`started`). Sibling of the lesson one. */
export function buildUnitLaunchScopeProps({ unitId, unitStatus }: BuildUnitLaunchScopePropsParams): PracticeUnitScopeProps {
  return {
    origin: 'mi_progreso_unit',
    scope: 'unit',
    unit_id: unitId,
    unit_status: toLessonAnalyticsStatus(unitStatus),
  };
}

/**
 * Scope properties of an ALREADY LOADED session (`completed`, `discarded`): the volatile KC
 * practice is reported with its origin, scope, volatility, KC and KC status; any other session
 * returns `{}` so no other flow changes its payload.
 *
 * The practised KC comes from `originKc.referenceId`, which the read DTO resolves from the
 * session's origin: it is therefore correct even when the session has no answers yet and after a
 * rollback has added other KCs to `sessionKCs`.
 */
export function buildLoadedSessionScopeProps({ isKcPractice, sessionData }: BuildLoadedSessionScopePropsParams): PracticeKcScopeProps | PracticeNoScopeProps {
  if (!isKcPractice) return {};

  return buildKcScopeProps(sessionData?.originKc?.referenceId ?? '');
}
